import { RCU_API, RCU_DISCOVERY, RCU_HTTP_PORT, RCU_PROTOCOL } from '../catalog/protocol';
import type { RcuIdentity } from '../model';

/**
 * Where the RCU is, and whether we know how to talk to it.
 *
 * Two ways to address one unit: the serial number the firmware advertises, which
 * resolves as `rcu-<serial>.local`, or an explicit IP address. The IP wins when
 * both are set, because name resolution for `.local` fails on some tablets
 * (notably Android) and is worth avoiding once the address is known.
 */

/** `888854` -> `rcu-888854.local`, the hostname the firmware advertises. */
export const serialHost = (serial: string): string =>
  `${RCU_DISCOVERY.HOST_PREFIX}${serial.trim()}${RCU_DISCOVERY.HOST_SUFFIX}`;

/** Letters, digits and dashes, at most `MAX_SERIAL_LENGTH` of them. */
export const isValidSerial = (serial: string): boolean => {
  const trimmed = serial.trim();
  return (
    trimmed.length > 0 &&
    trimmed.length <= RCU_DISCOVERY.MAX_SERIAL_LENGTH &&
    RCU_DISCOVERY.SERIAL_PATTERN.test(trimmed)
  );
};

/** Host to send to: the explicit address, else the name derived from the serial. */
export const rcuHost = (identity: RcuIdentity): string | null => {
  const address = identity.ipAddress.trim();
  if (address !== '') return address;
  return isValidSerial(identity.serialNumber) ? serialHost(identity.serialNumber) : null;
};

export const rcuOrigin = (identity: RcuIdentity): string | null => {
  const host = rcuHost(identity);
  return host === null ? null : `${RCU_PROTOCOL.DEFAULT}://${host}:${RCU_HTTP_PORT}`;
};

/** `http://rcu-888854.local:8900/command` */
export const commandUrl = (identity: RcuIdentity): string | null => {
  const origin = rcuOrigin(identity);
  return origin === null ? null : `${origin}${RCU_API.COMMAND_PATH}`;
};

/** `http://192.168.0.108:8900/status` */
export const statusUrl = (identity: RcuIdentity): string | null => {
  const origin = rcuOrigin(identity);
  return origin === null ? null : `${origin}${RCU_API.STATUS_PATH}`;
};
